import { useState, useEffect, useCallback, useMemo } from "react";
import {
  CreditCard, CheckCircle2, XCircle, RefreshCw,
  Loader2, AlertTriangle, Eye, Zap, ChevronLeft, ChevronRight, Send, Info,
} from "lucide-react";
import PageHeader from "@/components/dashboard/PageHeader";
import Modal from "@/components/dashboard/Modal";
import Button from "@/components/ui/Button";
import { cn } from "@/lib/cn";
import { useAuth } from "@/context/AuthContext";
import { getSubscription, createOrder, verifyPayment, cancelSubscription, listInvoices, getUsage, validateCoupon, contactSales } from "@/api/billing";
import { getCompanySettings } from "@/api/company";
import { LIMIT_ROWS, formatLimit, isUnlimited } from "@/data/plans";
import { usePlans } from "@/hooks/usePlans";
import { formatDate } from "@/lib/dates";
import { BASE_CURRENCY, formatMoney } from "@/lib/money";
import { loadRazorpay } from "@/lib/razorpay";
import { pageList } from "@/lib/pagination";
import { invoiceNumber } from "@/lib/format";
import PlanCard from "@/components/billing/PlanCard";

// ─── Helpers ────────────────────────────────────────────────────────────────
// Every amount carries its OWN currency. This used to hardcode a rupee sign and
// ".00", so a $49 invoice rendered as "₹49.00" and a ¥5,000 one — yen has no
// decimal places — as "₹5000.00". The currency comes off the record being shown
// (invoice.currency, subscription.currency), never assumed.
function fmt(amount, currency = BASE_CURRENCY) {
  if (!amount) return "Free";
  return formatMoney(Number(amount), currency);
}

const fmtDate = (v) => formatDate(v);

const STATUS_STYLE = {
  paid:    "bg-emerald-500/15 text-emerald-600 dark:text-emerald-300",
  refunded:"bg-sky-500/15 text-sky-600 dark:text-sky-300",
  failed:  "bg-rose-500/15 text-rose-600 dark:text-rose-300",
};

const SUB_STATUS_STYLE = {
  active:   "text-emerald-500",
  trialing: "text-sky-500",
  canceled: "text-slate-400",
  past_due: "text-amber-500",
};

// ─── Usage bar ──────────────────────────────────────────────────────────────
function UsageBar({ used, limit, label }) {
  // An unlimited quota has no meaningful "percent used" — drawing one at 0.2%
  // read as "you have used nothing" on an Enterprise plan that had run thousands.
  const unlimited = isUnlimited(limit);
  const pct = unlimited ? 0 : Math.min(100, Math.round((used / Math.max(limit, 1)) * 100));
  const color = pct >= 90 ? "bg-rose-500" : pct >= 70 ? "bg-amber-500" : "bg-brand-500";
  return (
    <div>
      <div className="mb-1 flex items-center justify-between text-xs">
        <span className="text-slate-500 dark:text-slate-400">{label}</span>
        <span className="font-semibold text-slate-700 dark:text-slate-200">
          {Number(used || 0).toLocaleString()} / {unlimited ? "∞" : formatLimit(limit)}
        </span>
      </div>
      <div className="h-2 w-full overflow-hidden rounded-full bg-slate-200 dark:bg-white/10">
        <div className={cn("h-full rounded-full transition-all", unlimited ? "bg-brand-500/40" : color)}
             style={{ width: unlimited ? "100%" : `${pct}%` }} />
      </div>
    </div>
  );
}

// ─── Main Page ───────────────────────────────────────────────────────────────
export default function TesterBilling() {
  const { user } = useAuth();
  // Country-adjusted plan pricing (#105) — /company/settings is admin-only, and
  // this page is also viewed read-only by non-admin roles, so a failure here
  // (403 for a tester, network hiccup, whatever) just leaves country unset and
  // usePlans falls back to exactly the base pricing it always showed.
  const [country, setCountry] = useState(null);
  useEffect(() => {
    let cancelled = false;
    getCompanySettings()
      .then((res) => { if (!cancelled) setCountry(res?.country || null); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);
  const { plans } = usePlans(country);
  const [sub, setSub] = useState(null);
  const [usageDetail, setUsageDetail] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);            // cancel-subscription action
  const [busyPlanId, setBusyPlanId] = useState(null); // the plan being checked out
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [cancelConfirm, setCancelConfirm] = useState(false);
  const [toast, setToast] = useState(null);
  const [coupon, setCoupon] = useState("");
  // Result of checking the typed code: { ok, message } or null. The field used to
  // accept anything, say "Applied when you subscribe to a plan below", and give
  // no verdict at all — a typo was discovered only when the Razorpay sheet opened
  // at the undiscounted price. validateCoupon has always existed in api/billing.js
  // and was called from nowhere.
  const [couponCheck, setCouponCheck] = useState(null);
  const [couponChecking, setCouponChecking] = useState(false);
  const [billingPeriod, setBillingPeriod] = useState("monthly"); // #89/#90
  const [contactOpen, setContactOpen] = useState(false);
  const [contactMessage, setContactMessage] = useState("");
  const [contactError, setContactError] = useState("");
  const [contactSending, setContactSending] = useState(false);
  const [invoicePage, setInvoicePage] = useState(1);

  const INVOICE_PAGE_SIZE = 8;
  const invoiceTotalPages = Math.max(1, Math.ceil(invoices.length / INVOICE_PAGE_SIZE));
  // A refresh (or a new invoice landing) can leave `invoicePage` past the end
  // — same reset rule used on What's New's pager, for the same reason.
  useEffect(() => { setInvoicePage((p) => Math.min(p, invoiceTotalPages)); }, [invoiceTotalPages]);
  const pagedInvoices = useMemo(
    () => invoices.slice((invoicePage - 1) * INVOICE_PAGE_SIZE, invoicePage * INVOICE_PAGE_SIZE),
    [invoices, invoicePage]
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // /usage carries the project and seat counters the run meter alone can't
      // show; a plan sells three quotas, so the page has to report all three.
      const [subData, invData, usageData] = await Promise.all([
        getSubscription(), listInvoices(), getUsage(),
      ]);
      setSub(subData);
      setInvoices(Array.isArray(invData) ? invData : []);
      setUsageDetail(usageData || null);
      return true;
    } catch (e) {
      setToast({ type: "error", message: e.message || "Failed to load billing data." });
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // The Refresh button called `load` directly — a re-fetch usually returns the
  // same numbers, so a working button looked pixel-identical to a dead one.
  // `load` already shows its own error banner on failure; only the success case
  // needed a message of its own here.
  async function handleRefresh() {
    if (await load()) setToast({ type: "success", message: "Billing refreshed." });
  }

  async function handleSelectPlan(plan) {
    // Free and "contact us" plans have nothing to check out. PlanCard normally
    // disables those buttons, so this is only reachable from a stale render —
    // but a bare `return` made the button look simply dead when it was.
    if (plan.price_inr === null || plan.price_inr === undefined) {
      setToast({ type: "error", message: "This plan has no self-serve price — use Contact sales." });
      return;
    }
    if (Number(plan.price_inr) === 0) {
      setToast({ type: "error", message: "That plan is free — there's nothing to check out." });
      return;
    }
    if (billingPeriod === "yearly"
        && (plan.price_inr_yearly === null || plan.price_inr_yearly === undefined)) {
      setToast({ type: "error", message: `${plan.name} isn't sold yearly — switch to Monthly.` });
      return;
    }
    if (busyPlanId) return;              // a checkout is already opening
    setBusyPlanId(plan.id);
    setToast(null);
    try {
      const order = await createOrder(plan.id, coupon.trim() || null, billingPeriod);

      // A cheaper plan/period while the current one is still paid for is
      // scheduled, not charged — nothing to check out, so no Razorpay widget
      // at all (#79, extended to period switches by #89/#90).
      if (order.scheduled) {
        const periodNote = order.billing_period === "yearly" ? " (billed yearly)" : "";
        setToast({
          type: "success",
          message: `Switching to ${plan.name}${periodNote} on ${fmtDate(order.effective_at)} — you keep your current plan until then.`,
        });
        await load();
        return;
      }

      const loaded = await loadRazorpay();
      if (!loaded) throw new Error("Failed to load Razorpay SDK.");

      await new Promise((resolve) => {
        const rzp = new window.Razorpay({
          key: order.key_id,
          subscription_id: order.subscription_id,   // ← subscription flow, enables QR
          name: "AI-QA-Engine",
          description: plan.name,
          prefill: {
            name: order.user_name,
            email: order.user_email,
            // No `contact` on purpose. It used to be hardcoded to 9999999999,
            // which pre-filled a fake number for every user. Razorpay asks for
            // one itself, and a saved Razorpay account fills it automatically.
          },
          theme: {
            color: "#7c3aed",
            // Razorpay's Standard Checkout is ALWAYS a full-screen overlay — it
            // has no option to render inside a container on our page. So the most
            // we can do is match its backdrop to the dashboard, otherwise the
            // default light backdrop reads as "the app navigated away".
            // NOTE: this must be a HEX code — Razorpay ignores rgba().
            backdrop_color: document.documentElement.classList.contains("dark")
              ? "#0f172a"   // slate-900, the dark dashboard background
              : "#f1f5f9",  // slate-100, the light dashboard background
          },
          handler: function(response) {
            verifyPayment({
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_subscription_id: response.razorpay_subscription_id,
              razorpay_signature: response.razorpay_signature,
              plan_id: plan.id,
              coupon_code: coupon.trim() || null,
              billing_period: billingPeriod,
            })
              .then((result) => {
                if (result.success) {
                  setToast({ type: "success", message: `Successfully upgraded to ${plan.name}!` });
                  load();
                }
                resolve();
              })
              .catch((err) => {
                setToast({ type: "error", message: err.message || "Payment verification failed." });
                resolve();
              });
          },
          modal: {
            ondismiss: () => {
              setBusyPlanId(null);
              resolve();
            },
          },
        });
        rzp.on("payment.failed", (response) => {
          setToast({ type: "error", message: response.error?.description || "Payment failed." });
          setBusyPlanId(null);
          resolve();
        });
        rzp.open();
      });
    } catch (e) {
      setToast({ type: "error", message: e.message || "Payment failed." });
    } finally {
      setBusyPlanId(null);
    }
  }


  // Custom plan's "Contact us" (#85) — a real in-app form that sends through
  // this app's own mailer, so it works regardless of whether the visitor's
  // machine has a mail client configured at all.
  function openContact() {
    setContactMessage("");
    setContactError("");
    setContactOpen(true);
  }

  async function handleContactSales() {
    const trimmed = contactMessage.trim();
    // Mirrors the backend's own min_length=10 — caught here first so the
    // request round-trip isn't the only thing standing between "empty" and
    // an error, but the server enforces the same bound regardless.
    if (trimmed.length < 10) {
      setContactError("Tell us a bit more — at least 10 characters.");
      return;
    }
    setContactSending(true);
    setContactError("");
    try {
      await contactSales(trimmed);
      setContactOpen(false);
      setToast({ type: "success", message: "Message sent — our sales team will be in touch." });
    } catch (e) {
      setContactError(e.message || "Couldn't send your message. Please try again.");
    } finally {
      setContactSending(false);
    }
  }

  // A coupon is not tied to a plan until Subscribe is clicked, so the code is
  // checked against a representative paid plan — that tells us whether the code
  // is real, live and unexhausted. The AMOUNT is plan-specific, so it is
  // deliberately not quoted here; the checkout sheet shows the true figure.
  const checkablePlan = useMemo(
    () => plans.find((p) => p.price_inr != null && Number(p.price_inr) > 0) || null,
    [plans]
  );

  async function handleCouponBlur() {
    const code = coupon.trim();
    if (!code) { setCouponCheck(null); return; }
    if (!checkablePlan) return;                       // catalog hasn't loaded yet
    setCouponChecking(true);
    try {
      const res = await validateCoupon(checkablePlan.id, code, billingPeriod);
      setCouponCheck(
        res && Number(res.discount) > 0
          ? { ok: true, message: `“${code}” accepted — your discount is applied at checkout.` }
          : { ok: false, message: `“${code}” gives no discount on these plans.` }
      );
    } catch (e) {
      setCouponCheck({ ok: false, message: e.message || `“${code}” isn't a valid coupon.` });
    } finally {
      setCouponChecking(false);
    }
  }

  async function handleCancel() {
    setBusy(true);
    try {
      await cancelSubscription();
      setToast({ type: "success", message: "Subscription canceled. Access continues until period end." });
      setCancelConfirm(false);
      await load();
    } catch (e) {
      setToast({ type: "error", message: e.message || "Failed to cancel." });
    } finally {
      setBusy(false);
    }
  }

  // The EFFECTIVE plan — what the backend actually enforces right now. A
  // cancelled subscription past its paid period is on the free tier, and the
  // card marked "Current Plan" has to be the one whose limits apply.
  const currentPlanId = sub?.effective_plan_id || sub?.plan_id || "free_trial";
  // May be undefined if an admin has retired the plan this company is on — the
  // banner falls back to the name the subscription itself reports.
  const currentPlan = plans.find((p) => p.id === currentPlanId);
  const usage = sub?.usage;
  const quotaRows = usageDetail
    ? LIMIT_ROWS.map((row) => ({
        key: row.key,
        label: row.label,
        used: usageDetail[`${row.usageKey}${row.usageKey === "runs" ? "_this_month" : "_used"}`] ?? 0,
        limit: usageDetail[`${row.usageKey}_limit`] ?? 0,
      }))
    : [];

  return (
    <>
      <PageHeader
        title="Billing & Plan"
        subtitle="Manage your subscription and view invoice history."
        actions={
          <Button variant="outline" size="sm" onClick={handleRefresh} disabled={loading}>
            <RefreshCw size={16} className={loading ? "animate-spin" : ""} /> Refresh
          </Button>
        }
      />

      {/* Billing instructions (#92) — states the two rules that actually
          govern every plan change on this page, since neither is obvious
          from the buttons alone: an upgrade charges you now, a downgrade
          waits for the plan you already paid for to run out first. */}
      <div className="mb-5 flex items-start gap-2.5 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600 dark:border-white/10 dark:bg-white/5 dark:text-slate-300">
        <Info size={16} className="mt-0.5 shrink-0 text-brand-500" />
        <p>
          <strong>How plan changes work:</strong> upgrading to a higher plan takes effect immediately and is
          charged right away. Downgrading to a cheaper plan is scheduled instead — you keep your current
          plan&apos;s access until it runs out, and nothing is charged until the switch actually happens.
        </p>
      </div>

      {/* Toast */}
      {toast && (
        <div className={cn(
          "mb-5 flex items-center gap-2 rounded-xl border px-4 py-2.5 text-sm",
          toast.type === "error"
            ? "border-rose-200 bg-rose-50/70 text-rose-700 dark:border-rose-500/20 dark:bg-rose-500/10 dark:text-rose-200"
            : "border-emerald-200 bg-emerald-50/70 text-emerald-700 dark:border-emerald-500/20 dark:bg-emerald-500/10 dark:text-emerald-200"
        )}>
          {toast.type === "error" ? <AlertTriangle size={15} /> : <CheckCircle2 size={15} />}
          <span className="flex-1">{toast.message}</span>
          <button type="button" onClick={() => setToast(null)} className="text-xs opacity-60 hover:opacity-100">✕</button>
        </div>
      )}

      {/* Test mode banner removed — payment works, no guidance needed */}

      {loading ? (
        <div className="flex items-center justify-center py-20 text-slate-400">
          <Loader2 size={26} className="animate-spin" />
        </div>
      ) : (
        <>
          {/* Current Plan Banner */}
          {sub && (
            <div className="card mb-6 overflow-hidden">
              <div className="flex flex-col gap-4 bg-gradient-to-r from-brand-600/10 to-brand-500/5 p-5 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-brand-600 dark:text-brand-400">Current Plan</p>
                  <h2 className="mt-1 text-2xl font-bold text-slate-900 dark:text-white">
                    {currentPlan?.name || sub.plan_name || currentPlanId}
                    {sub.billing_period === "yearly" && (
                      <span className="ml-2 align-middle text-xs font-semibold uppercase tracking-wide text-brand-600 dark:text-brand-400">Yearly</span>
                    )}
                  </h2>
                  <div className="mt-1 flex flex-wrap items-center gap-3 text-sm">
                    <span className={cn("font-semibold capitalize", SUB_STATUS_STYLE[sub.status] || "text-slate-400")}>
                      ● {sub.status}
                    </span>
                    {sub.current_period_end && (
                      <span className="text-slate-500 dark:text-slate-400">
                        Renews {fmtDate(sub.current_period_end)}
                      </span>
                    )}
                    {sub.amount_paid > 0 && (
                      <span className="text-slate-500 dark:text-slate-400">
                        Last paid: {fmt(sub.amount_paid, sub.currency)}
                      </span>
                    )}
                  </div>
                  {user?.name && (
                    <p className="mt-2 text-xs text-slate-400">Plan selected by {user.name}</p>
                  )}
                  {/* The whole point of #79: say plainly that the current plan
                      keeps running until it actually ends, not just silently
                      not-switch — a downgrade with zero visible confirmation
                      reads exactly like a downgrade that silently failed. */}
                  {sub.pending_plan_id && (
                    <p className="mt-2 text-xs font-medium text-amber-600 dark:text-amber-400">
                      Switching to {plans.find((p) => p.id === sub.pending_plan_id)?.name || sub.pending_plan_id}
                      {sub.pending_billing_period === "yearly" && " (billed yearly)"}
                      {sub.current_period_end && <> on {fmtDate(sub.current_period_end)}</>} — you keep {currentPlan?.name || currentPlanId} until then.
                    </p>
                  )}
                </div>
                {sub.status === "active" && currentPlanId !== "free_trial" && (
                  <Button variant="outline" size="sm" onClick={() => setCancelConfirm(true)}>
                    <XCircle size={15} /> Cancel subscription
                  </Button>
                )}
              </div>

              {/* Usage bars */}
              {usage && (
                <div className="border-t border-slate-200 p-5 dark:border-white/10">
                  <div className="flex items-center gap-2 mb-3">
                    <Zap size={15} className="text-brand-500" />
                    <span className="text-sm font-semibold text-slate-800 dark:text-white">Your plan usage</span>
                    {usage.percent_used >= 90 && (
                      <span className="rounded-full bg-rose-500/15 px-2 py-0.5 text-[0.625rem] font-bold text-rose-600 dark:text-rose-300">
                        NEAR LIMIT
                      </span>
                    )}
                  </div>
                  {/* All three quotas the plan sells, not just runs — a company
                      could hit the project or seat cap with no warning here. */}
                  <div className="grid gap-4 sm:grid-cols-3">
                    {(quotaRows.length ? quotaRows : [{
                      key: "max_runs_per_month", label: "Test runs",
                      used: usage.runs_this_month, limit: usage.runs_limit,
                    }]).map((q) => (
                      <UsageBar key={q.key} used={q.used} limit={q.limit}
                        label={q.key === "max_runs_per_month" ? "Test runs (this month)" : q.label} />
                    ))}
                  </div>
                  {/* Not a plan quota — no limit to bar-chart — just headcount for
                      visibility, since neither role consumes a tester seat. */}
                  {usage.dev_pm_count > 0 && (
                    <p className="mt-3 text-xs text-slate-500 dark:text-slate-400">
                      + {usage.dev_pm_count} developer/PM seat{usage.dev_pm_count === 1 ? "" : "s"} (unlimited, not counted above)
                    </p>
                  )}
                  {usage.runs_remaining === 0 && (
                    <p className="mt-2 text-xs font-medium text-rose-600 dark:text-rose-400">
                      Run limit reached — upgrade to continue testing.
                    </p>
                  )}
                  {sub.plan_id !== currentPlanId && (
                    <p className="mt-2 text-xs font-medium text-amber-600 dark:text-amber-400">
                      Your {String(sub.plan_id || "").replace(/_/g, " ")} subscription has ended — these are free-tier limits until you resubscribe.
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Plan Cards */}
          {/* flex-wrap so a narrow screen stacks the coupon field under the
              heading instead of crushing both into one row (#84's real
              complaint doubled as an unlabeled, unexplained input — a label +
              a hint for what it actually does, not a live preview, since it's
              not tied to any one plan until Subscribe is clicked). */}
          <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
            <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-200">
              Available Plans
              {/* Makes the country-adjustment (#105) visible rather than a
                  silent number change nobody notices until checkout. */}
              {country && <span className="ml-2 font-normal text-slate-400">· pricing for {country}</span>}
            </h3>
            <div className="flex flex-col items-start gap-1 sm:items-end">
              <label htmlFor="billing-coupon" className="text-xs font-medium text-slate-500 dark:text-slate-400">
                Have a coupon?
              </label>
              <input
                id="billing-coupon"
                value={coupon}
                onChange={(e) => { setCoupon(e.target.value.toUpperCase()); setCouponCheck(null); }}
                onBlur={handleCouponBlur}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleCouponBlur(); } }}
                placeholder="Coupon code"
                aria-describedby="billing-coupon-status"
                className={cn(
                  "w-40 rounded-lg border bg-white px-3 py-1.5 text-sm font-mono uppercase placeholder:font-sans placeholder:normal-case dark:bg-white/5 dark:text-slate-200",
                  couponCheck && !couponCheck.ok
                    ? "border-rose-300 dark:border-rose-500/40"
                    : "border-slate-200 dark:border-white/10"
                )}
              />
              <p id="billing-coupon-status" className="max-w-[16rem] text-[0.6875rem] sm:text-right">
                {couponChecking ? (
                  <span className="text-slate-400">Checking…</span>
                ) : couponCheck ? (
                  <span className={couponCheck.ok
                    ? "font-medium text-emerald-600 dark:text-emerald-400"
                    : "font-medium text-rose-600 dark:text-rose-400"}>
                    {couponCheck.message}
                  </span>
                ) : coupon.trim() ? (
                  <span className="text-slate-400">Applied when you subscribe to a plan below.</span>
                ) : null}
              </p>
            </div>
          </div>

          {/* Monthly/yearly toggle (#89/#90) — only changes what the cards
              below SHOW; billingPeriod travels with the actual checkout call. */}
          <div className="mb-5 flex justify-center">
            <div className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white p-1 dark:border-white/10 dark:bg-white/5">
              <button
                type="button"
                onClick={() => setBillingPeriod("monthly")}
                aria-pressed={billingPeriod === "monthly"}
                className={cn(
                  "rounded-full px-4 py-1.5 text-sm font-semibold transition-colors",
                  billingPeriod === "monthly"
                    ? "bg-brand-600 text-white shadow-soft"
                    : "text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
                )}
              >
                Monthly
              </button>
              <button
                type="button"
                onClick={() => setBillingPeriod("yearly")}
                aria-pressed={billingPeriod === "yearly"}
                className={cn(
                  "flex items-center gap-1.5 rounded-full px-4 py-1.5 text-sm font-semibold transition-colors",
                  billingPeriod === "yearly"
                    ? "bg-brand-600 text-white shadow-soft"
                    : "text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
                )}
              >
                Yearly
                <span className={cn(
                  "rounded-full px-1.5 py-0.5 text-[0.625rem] font-bold",
                  billingPeriod === "yearly" ? "bg-white/20 text-white" : "bg-emerald-500/15 text-emerald-600 dark:text-emerald-300"
                )}>
                  -20%
                </span>
              </button>
            </div>
          </div>
          <div className="mb-8 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            {plans.map((plan) => (
              <PlanCard
                key={plan.id}
                plan={plan}
                currentPlanId={currentPlanId}
                onSelect={handleSelectPlan}
                onContactUs={openContact}
                busyPlanId={busyPlanId}
                billingPeriod={billingPeriod}
              />
            ))}
          </div>

          {/* Invoice History */}
          <div className="card overflow-hidden">
            <div className="border-b border-slate-200 px-5 py-4 dark:border-white/10">
              <h3 className="text-sm font-semibold text-slate-800 dark:text-white">Invoice History</h3>
            </div>
            {invoices.length === 0 ? (
              <div className="px-5 py-10 text-center text-sm text-slate-400">
                <CreditCard size={28} className="mx-auto mb-2 opacity-30" />
                No invoices yet. Your first payment will appear here.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[540px] border-collapse text-sm">
                  <thead className="bg-slate-50/95 dark:bg-ink-900/95">
                    <tr>
                      {["DATE", "AMOUNT", "PERIOD", "STATUS", ""].map((h) => (
                        <th key={h} className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-white/5">
                    {pagedInvoices.map((inv) => (
                      <tr key={inv.id} className="hover:bg-slate-50 dark:hover:bg-white/5">
                        <td className="px-5 py-3 text-slate-700 dark:text-slate-200">{fmtDate(inv.created_at)}</td>
                        <td className="px-5 py-3 font-semibold text-slate-800 dark:text-white">{fmt(inv.amount, inv.currency)}</td>
                        <td className="px-5 py-3 text-xs text-slate-500 dark:text-slate-400">
                          {fmtDate(inv.period_start)} — {fmtDate(inv.period_end)}
                        </td>
                        <td className="px-5 py-3">
                          <span className={cn("rounded-full px-2.5 py-0.5 text-xs font-bold uppercase", STATUS_STYLE[inv.status] || STATUS_STYLE.paid)}>
                            ● {inv.status}
                          </span>
                        </td>
                        <td className="px-5 py-3">
                          <button
                            onClick={() => setSelectedInvoice(inv)}
                            className="grid h-8 w-8 place-items-center rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-white/5"
                            title="View"
                          >
                            <Eye size={15} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {/* Same pager chrome as What's New's, so a second list in the app
                doesn't invent its own differently-styled control (#88). */}
            {invoiceTotalPages > 1 && (
              <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 px-5 py-3 dark:border-white/10">
                <p className="text-xs text-slate-400">
                  {invoices.length.toLocaleString()} invoice{invoices.length === 1 ? "" : "s"}
                </p>
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => setInvoicePage((p) => Math.max(1, p - 1))}
                    disabled={invoicePage === 1}
                    aria-label="Previous page"
                    className="grid h-8 w-8 place-items-center rounded-lg border border-slate-200 text-slate-500 transition-colors hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-40 dark:border-white/10 dark:text-slate-300 dark:hover:bg-white/5"
                  >
                    <ChevronLeft size={15} />
                  </button>
                  {pageList(invoicePage, invoiceTotalPages).map((p, i) =>
                    p === "…" ? (
                      <span key={`gap-${i}`} className="px-1.5 text-slate-400">…</span>
                    ) : (
                      <button
                        key={p}
                        type="button"
                        onClick={() => setInvoicePage(p)}
                        aria-current={p === invoicePage ? "page" : undefined}
                        className={cn(
                          "h-8 min-w-8 rounded-lg px-2 text-xs font-semibold transition-colors",
                          p === invoicePage
                            ? "bg-brand-600 text-white shadow-soft"
                            : "border border-slate-200 text-slate-600 hover:bg-slate-100 dark:border-white/10 dark:text-slate-300 dark:hover:bg-white/5"
                        )}
                      >
                        {p}
                      </button>
                    )
                  )}
                  <button
                    type="button"
                    onClick={() => setInvoicePage((p) => Math.min(invoiceTotalPages, p + 1))}
                    disabled={invoicePage === invoiceTotalPages}
                    aria-label="Next page"
                    className="grid h-8 w-8 place-items-center rounded-lg border border-slate-200 text-slate-500 transition-colors hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-40 dark:border-white/10 dark:text-slate-300 dark:hover:bg-white/5"
                  >
                    <ChevronRight size={15} />
                  </button>
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {/* Invoice detail modal */}
      <Modal
        open={!!selectedInvoice}
        onClose={() => setSelectedInvoice(null)}
        title=""
        size="md"
        footer={
          <div className="flex items-center justify-between gap-2">
            <Button variant="outline" size="sm" onClick={() => setSelectedInvoice(null)}>Close</Button>
            <Button size="sm" onClick={() => window.print()} className="gap-2">
              <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/><rect x="6" y="14" width="12" height="8"/></svg>
              Print Invoice
            </Button>
          </div>
        }
      >
        {selectedInvoice && (() => {
          const plan = plans.find((p) => p.id === selectedInvoice.plan_id);
          const isPaid = selectedInvoice.status === "paid";
          return (
            <div className="invoice-print space-y-6">
              {/* Header */}
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xl font-bold text-slate-900 dark:text-white">AI-QA-Engine</p>
                  <p className="mt-0.5 text-xs text-slate-400">ai-qa-engine.com</p>
                </div>
                <div className="text-right">
                  <span className={cn(
                    "inline-block rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wide",
                    isPaid ? "bg-emerald-500/15 text-emerald-600 dark:text-emerald-300"
                           : "bg-rose-500/15 text-rose-600 dark:text-rose-300"
                  )}>
                    ● {selectedInvoice.status}
                  </span>
                  <p className="mt-1 text-xs text-slate-400">
                    Invoice #{invoiceNumber(selectedInvoice.id)}
                  </p>
                </div>
              </div>

              {/* Divider */}
              <div className="border-t border-slate-200 dark:border-white/10" />

              {/* Bill to + dates */}
              <div className="grid grid-cols-1 gap-4 text-sm sm:grid-cols-2">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-1">Billed To</p>
                  <p className="font-medium text-slate-800 dark:text-white">{selectedInvoice.user_email || "—"}</p>
                  <p className="text-xs text-slate-400 mt-0.5">AI-QA-Engine account</p>
                </div>
                <div className="text-right">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-1">Invoice Date</p>
                  <p className="font-medium text-slate-800 dark:text-white">{fmtDate(selectedInvoice.created_at)}</p>
                  <p className="text-xs text-slate-400 mt-0.5">
                    Period: {fmtDate(selectedInvoice.period_start)} — {fmtDate(selectedInvoice.period_end)}
                  </p>
                </div>
              </div>

              {/* Line items */}
              <div className="overflow-hidden rounded-xl border border-slate-200 dark:border-white/10">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 dark:bg-white/5">
                    <tr>
                      <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">Description</th>
                      <th className="px-4 py-2.5 text-right text-xs font-semibold uppercase tracking-wide text-slate-500">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-t border-slate-200 dark:border-white/10">
                      <td className="px-4 py-3">
                        <p className="font-medium text-slate-800 dark:text-white">{plan?.name || selectedInvoice.plan_id}</p>
                        <p className="text-xs text-slate-400">
                          {selectedInvoice.billing_period === "yearly" ? "Yearly" : "Monthly"} subscription · {selectedInvoice.currency}
                        </p>
                      </td>
                      <td className="px-4 py-3 text-right font-semibold text-slate-800 dark:text-white">
                        {fmt(selectedInvoice.amount, selectedInvoice.currency)}
                      </td>
                    </tr>
                  </tbody>
                  <tfoot className="border-t-2 border-slate-200 dark:border-white/10">
                    <tr>
                      <td className="px-4 py-3 text-sm font-bold text-slate-800 dark:text-white">Total</td>
                      <td className="px-4 py-3 text-right text-lg font-bold text-slate-900 dark:text-white">
                        {fmt(selectedInvoice.amount, selectedInvoice.currency)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              {/* Payment reference */}
              {selectedInvoice.razorpay_payment_id && (
                <div className="rounded-xl bg-slate-50 px-4 py-3 dark:bg-white/5">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-1">Payment Reference</p>
                  <p className="font-mono text-xs text-slate-700 dark:text-slate-200 break-all">
                    {selectedInvoice.razorpay_payment_id}
                  </p>
                </div>
              )}

              <p className="text-center text-xs text-slate-400">
                Thank you for using AI-QA-Engine. Questions? Email support@ai-qa-engine.com
              </p>
            </div>
          );
        })()}
      </Modal>

      {/* Cancel confirmation */}
      <Modal
        open={cancelConfirm}
        onClose={() => setCancelConfirm(false)}
        title="Cancel subscription?"
        size="md"
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={() => setCancelConfirm(false)}>Keep plan</Button>
            <Button size="sm" variant="danger" onClick={handleCancel} disabled={busy}>
              {busy ? <Loader2 size={14} className="animate-spin" /> : <XCircle size={14} />}
              Cancel subscription
            </Button>
          </div>
        }
      >
        <p className="text-sm text-slate-600 dark:text-slate-300">
          Your <strong>{currentPlan?.name || sub?.plan_name || currentPlanId}</strong> subscription will be canceled.
          You&apos;ll retain access until <strong>{fmtDate(sub?.current_period_end)}</strong>, then revert to the Free Trial limits.
        </p>
      </Modal>

      {/* Contact sales (#85) — replaces a bare mailto link that silently did
          nothing on a machine with no mail client configured. */}
      <Modal
        open={contactOpen}
        onClose={() => (contactSending ? null : setContactOpen(false))}
        title="Contact sales"
        size="md"
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={() => setContactOpen(false)} disabled={contactSending}>
              Cancel
            </Button>
            <Button size="sm" onClick={handleContactSales} disabled={contactSending}>
              {contactSending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
              Send message
            </Button>
          </div>
        }
      >
        <div className="space-y-3">
          <p className="text-sm text-slate-600 dark:text-slate-300">
            Tell us about your team and what you need — someone from sales will follow up at <strong>{user?.email}</strong>.
          </p>
          <textarea
            value={contactMessage}
            onChange={(e) => { setContactMessage(e.target.value); setContactError(""); }}
            rows={5}
            maxLength={2000}
            placeholder="Tell us about your team, your apps and what you need…"
            className={cn(
              "w-full resize-none rounded-xl border bg-slate-50 p-3 text-sm text-slate-800 placeholder:text-slate-400 focus:bg-white focus:outline-none focus:ring-4 focus:ring-brand-500/10 dark:bg-white/5 dark:text-white",
              contactError ? "border-rose-300 dark:border-rose-500/40" : "border-slate-200 focus:border-brand-400 dark:border-white/10"
            )}
          />
          {contactError && (
            <p role="alert" className="flex items-center gap-1.5 text-xs text-rose-600 dark:text-rose-400">
              <AlertTriangle size={13} className="shrink-0" /> {contactError}
            </p>
          )}
        </div>
      </Modal>
    </>
  );
}
